import React, { useState } from "react";

export interface AvatarProps {
  /** The text to generate initials from (usually a name) */
  text: string;
  /** Optional image URL to show as avatar (e.g. professional/client picture) */
  src?: string | null;
  /** Size of the avatar */
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
  /** Color variant (used when no src or image fails to load) */
  variant?: "blue" | "emerald" | "purple" | "gray" | "primary" | "white";
  /** Custom className to override styles */
  className?: string;
  /** Click handler */
  onClick?: () => void;
  /** Whether the avatar should have a subtle opacity (for overlay effects) */
  subtle?: boolean;
}

export const Avatar: React.FC<AvatarProps> = ({
  text,
  src,
  size = "md",
  variant = "blue",
  className = "",
  onClick,
  subtle = false,
}) => {
  const [imgError, setImgError] = useState(false);
  const showImage = src && !imgError;

  // Generate initials from text
  const getInitials = (text: string): string => {
    const words = text.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }
    return words
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join("");
  };

  // Size classes
  const sizeClasses = {
    xs: "h-6 w-6 text-xs",
    sm: "h-8 w-8 text-sm",
    md: "h-10 w-10 text-sm",
    lg: "h-12 w-12 text-base",
    xl: "h-16 w-16 text-lg",
    "2xl": "h-24 w-24 text-xl",
  };

  // Variant classes
  const variantClasses = {
    blue: "bg-gradient-to-br from-sky-500 to-sky-600 text-white",
    emerald: "bg-gradient-to-r from-emerald-500 to-emerald-600 text-white",
    purple: "bg-gradient-to-br from-violet-500 to-violet-600 text-white",
    gray: "bg-gradient-to-br from-gray-500 to-gray-600 text-white",
    primary: "bg-gradient-to-r from-primary-500 to-primary-600 text-white",
    white: subtle ? "bg-white/20 text-white" : "bg-white text-gray-700",
  };

  const fontWeightClass =
    size === "xs" || size === "sm" ? "font-semibold" : "font-bold";

  const baseClasses = `
    flex-shrink-0 rounded-full flex items-center justify-center overflow-hidden
    ${sizeClasses[size]}
    ${!showImage ? variantClasses[variant] : ""}
    ${fontWeightClass}
    ${
      onClick
        ? "cursor-pointer hover:scale-105 transition-transform duration-200"
        : ""
    }
    ${className}
  `
    .trim()
    .replace(/\s+/g, " ");

  return (
    <div className={baseClasses} onClick={onClick}>
      {showImage ? (
        <img
          src={src}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        getInitials(text)
      )}
    </div>
  );
};

export default Avatar;
